import { types, flow } from 'mobx-state-tree';
import getAppRoot from './utils/getAppRoot';
import getAppEnv from './utils/getAppEnv';
import ProjectIdQuery from './queries/projects/ProjectIdQuery';
import ProjectPaginatedQuery from './queries/projects/ProjectPaginatedQuery';
import { ProjectDtoModelType } from './models';
import { logglyLogger } from '../../services/logglyLogger';
import SelectedProjectQuery from './queries/projects/SelectedProjectQuery';

export interface ProjectListingQuery {
  offset: number; // start index
  limit: number;
  startDate?: string;
}

const Workspace = types.model('Workspace')
  .props({
    areWorkspacesFetching: types.optional(types.boolean, true),
  })
  .volatile(() => ({
    isWorkspaceFetching: false,
  }))
  .actions((self) => ({
    fetchWorkspaceById: flow(function* (workspaceId: string) {
      if (!workspaceId || !Number(workspaceId)) {
        logglyLogger.log({ error: 'No Workspace ID', info: 'Failed to fetch project - no id provided' });
        return Promise.reject(new Error('NO_PROJECT_ID'));
      }

      try {
        self.isWorkspaceFetching = true;
        const fetchProject = async (projectId: string): Promise<any> => {
          await getAppRoot(self).model.queryProjectById({ projectId }, SelectedProjectQuery, { fetchPolicy: 'no-cache' });
          return Promise.resolve();
        };
        yield fetchProject(workspaceId);
      } catch (err) {
        return Promise.reject();
      } finally {
        self.isWorkspaceFetching = false;
      }

      return Promise.resolve();
    }),
    create: flow(function* (name: string) {
      const project = yield getAppRoot(self).model.mutateCreateProject({
        project: {
          name,
        },
      }, ProjectIdQuery).currentPromise();

      return project.createProject;
    }),
    patch: flow(function* (name: string, id: number) {
      const project = yield getAppRoot(self).model.mutatePatchProject({
        project: {
          name,
          id,
        },
      }, ProjectIdQuery).currentPromise();

      return project.patchProject;
    }),
    createOptimizedWorkspaceFromJob: flow(function* (jobId: string) {
      const { data } = yield getAppEnv(self).api.post(`/optimize/visualize/${jobId}`);

      return data;
    }),
    delete: flow(function* (projectId: string) {
      const deleteProject = async (id: string): Promise<any> => {
        await getAppRoot(self).model.deleteProject(id);
        return Promise.resolve();
      };

      yield deleteProject(projectId);
    }),
    fetchListing: flow(function* (options: ProjectListingQuery): Generator<Promise<void>, ProjectDtoModelType[], void> {
      self.areWorkspacesFetching = true;
      const pageSize = 20;

      const fetchProjectsPaginated = async (offset: number): Promise<any> => {
        const res = await getAppRoot(self).model.queryProjectsPaginated({
          ...options,
        }, ProjectPaginatedQuery, { fetchPolicy: 'no-cache' }).promise; // cache-and-network?

        if (res.projectsPaginated.hasNextPage && offset) {
          return fetchProjectsPaginated(offset + pageSize);
        }

        return Promise.resolve();
      };

      yield fetchProjectsPaginated(0);

      self.areWorkspacesFetching = false;

      const workspaces = Array.from<ProjectDtoModelType>(getAppRoot(self).model.projectDtos.values());
      if (options?.startDate) {
        // Re-sort list for new project afterward by id (to mimic backend sorting)
        return workspaces.sort((a: ProjectDtoModelType, b: ProjectDtoModelType) => Number(b.id) - Number(a.id));
      }

      return workspaces;
    }) as (options: ProjectListingQuery) => Promise<ProjectDtoModelType[]>,
  }));

export default Workspace;
